import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { Location } from '@reach/router';

export const buildSocialImage = (image) => {
  if (!image) return null;
  return {
    url: image.fixed.src.indexOf('//') === 0 ? `https:${image.fixed.src}` : image.fixed.src,
    width: image.fixed.width,
    height: image.fixed.height,
    alt: image.title,
  };
};

export const MetadataContainer = ({
  title, description, image, location, siteMetadata,
}) => {
  const isHome = location.pathname === '/';
  const browsertitle = isHome ? title : `${title} | ${siteMetadata.siteTitle}`;
  const canonicalUrl = siteMetadata.siteUrl + location.pathname;

  return (
    <Helmet
      title={browsertitle}
      htmlAttributes={{
        lang: 'en',
      }}
    >
      {/* General tags */}
      {description && <meta name="description" content={description} />}
      <link rel="icon" type="image/png" href="/images/favicon-96x96.png" sizes="96x96" />
      <link rel="icon" type="image/svg+xml" href="/images/favicon.svg" />
      <link rel="shortcut icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/images/apple-touch-icon.png" />
      <meta name="apple-mobile-web-app-title" content="CME Brand Central" />
      <link rel="manifest" href="/images/site.webmanifest" />

      {/* OpenGraph tags */}
      <meta property="og:site_name" content={siteMetadata.siteTitle} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      {image && image.url && <meta property="og:image" content={image.url} />}
      {image && image.width && <meta property="og:image:width" content={image.width} />}
      {image && image.height && <meta property="og:image:height" content={image.height} />}
      {image && image.alt && <meta property="og:image:alt" content={image.alt} />}

      {/* Twitter Card tags */}
      {/* Twitter uses some og tags as fallback https://developer.twitter.com/en/docs/tweets/optimize-with-cards/overview/markup */}
      <meta property="twitter:card" content="summary" />
      {image && image.alt && <meta property="twitter:image:alt" content={image.alt} />}
    </Helmet>
  );
};

MetadataContainer.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  siteMetadata: PropTypes.shape({
    siteTitle: PropTypes.string,
    siteUrl: PropTypes.string,
  }).isRequired,
};

MetadataContainer.defaultProps = {
  description: '',
  image: null,
};

const MetadataWithQuery = (props) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteTitle
            siteUrl
          }
        }
      }
    `,
  );

  return (
    <Location>
      {({ location }) => (
        <MetadataContainer
          {...props}
          location={location}
          siteMetadata={site ? site.siteMetadata : {}}
        />
      )}
    </Location>
  );
};

export default MetadataWithQuery;
